import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Helmet } from "react-helmet";
import { Layout } from "@/components/layout/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Phone, Mail, User, Building } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/api";

export default function ContactAgentPage() {
  const searchParams = new URLSearchParams(window.location.search);
  const propertyId = searchParams.get("propertyId");
  
  const [location, setLocation] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: user?.fullName || "",
    email: user?.email || "",
    phone: user?.phone || "",
    message: "",
  });

  // Fetch property details if propertyId is provided
  const { data: property, isLoading: isLoadingProperty } = useQuery({
    queryKey: ["properties", propertyId],
    queryFn: async () => {
      if (!propertyId) return null;
      const res = await apiRequest('GET', `properties/${propertyId}`);
      // if (!res.ok) throw new Error("Failed to fetch property");
      // return res.json();
      return res;
    },
    enabled: !!propertyId,
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Send the contact form data to the backend to trigger the email
      await apiRequest('POST', 'contact-agent', contactForm);
      
      toast({
        title: "Request sent",
        description: "An agent will contact you shortly with property information and pricing.",
      });
      
      setContactForm({
        ...contactForm,
        message: "",
      });
      
      // Redirect back to properties page after successful submission
      setTimeout(() => {
        setLocation("/properties");
      }, 2000);
      
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send your request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>Contact an Agent | StayMiddle</title>
      </Helmet>
      <div className="container max-w-4xl py-12">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
          <div className="md:col-span-3">
            <Card>
              <CardHeader>
                <CardTitle>Contact an Agent</CardTitle>
                <CardDescription>
                  Fill out the form below to get pricing and more information about this property
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {property && (
                    <div className="mb-6 p-4 bg-muted rounded-lg">
                      <h3 className="font-medium mb-2">Property Information</h3>
                      <p className="text-sm text-muted-foreground flex items-center">
                        <Building className="w-4 h-4 mr-2" />
                        {property.title}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {property.bedrooms === 0 ? "Studio" : `${property.bedrooms} bedrooms`} · {Number(property.bathrooms).toFixed(1)} bathrooms · Up to {property.maxGuests} guests
                      </p>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label htmlFor="name">Your Name</Label>
                    <div className="relative">
                      <Input 
                        id="name" 
                        name="name" 
                        value={contactForm.name}
                        onChange={handleInputChange}
                        required
                        className="pl-10"
                      />
                      <User className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <div className="relative">
                      <Input 
                        id="email" 
                        name="email" 
                        type="email" 
                        value={contactForm.email}
                        onChange={handleInputChange}
                        required
                        className="pl-10"
                      />
                      <Mail className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <div className="relative">
                      <Input 
                        id="phone" 
                        name="phone" 
                        value={contactForm.phone}
                        onChange={handleInputChange}
                        required
                        className="pl-10"
                      />
                      <Phone className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="message">Message</Label>
                    <Textarea 
                      id="message" 
                      name="message" 
                      placeholder="Please let us know your travel dates and any specific requirements"
                      value={contactForm.message}
                      onChange={handleInputChange}
                      rows={4}
                      required
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      "Submit Request"
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
          
          <div className="md:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Why Contact an Agent?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 border rounded-lg">
                  <h4 className="font-semibold text-sm mb-2">Get the Best Pricing</h4>
                  <p className="text-sm text-muted-foreground">
                    Our agents can provide special offers and customized pricing based on your stay duration.
                  </p>
                </div>
                
                <div className="p-3 border rounded-lg">
                  <h4 className="font-semibold text-sm mb-2">Special Requests</h4>
                  <p className="text-sm text-muted-foreground">
                    Need airport pickup, early check-in, or special arrangements? Our agents can help.
                  </p>
                </div>
                
                <div className="p-3 border rounded-lg">
                  <h4 className="font-semibold text-sm mb-2">Local Expertise</h4>
                  <p className="text-sm text-muted-foreground">
                    Get insider recommendations on the area, dining, and activities from local experts.
                  </p>
                </div>
                
                <div className="p-3 border rounded-lg">
                  <h4 className="font-semibold text-sm mb-2">Quick Response</h4>
                  <p className="text-sm text-muted-foreground">
                    Our agents typically respond within 2 hours during business hours.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}